import BaseSearchController from "./base_search_controller"

const VENUE_PER_PAGE = 24

/**
 *
 * This controller manages the venue search functionality.
 * It intercepts changes made to the filters directly in the main form or in the subcomponents managed by the outlets.
 * With each change, it updates the URL parameters and submits the form to get the corresponding results.
 * It also handles the connection between the search form and the Google Maps.
 **/
export default class extends BaseSearchController {
  static targets = [
    "centerPoint",
    "form",
    "map",
    "productInfoWindow",
    "productList",
    "productListItem",
    "results",
    "queryInfo",
    "noResultsMessage",
    "modalBody",
    "mobileFilterButton",
    "packagesPopoverButton",
    "secondarySpaces",
    "modalPackagesButtonControl",
    "modalPackagesSection",
    "modalHousingSection",
    "modalKindTagsSection",
    "modalStandingTagsSection",
    "hasPromotionalOffer"
  ]

  secondarySpacesTargetDisconnected(target) {
    this.liveSecondarySpaceRows = Array.from(target.querySelectorAll('[class*="nested-form-wrapper"]'))
  }

  secondarySpacesTargetConnected(target) {
    if (!this.liveSecondarySpaceRows) return

    target.querySelectorAll('[class*="nested-form-wrapper"]').forEach((row) => row.remove())
    const insertionPoint = target.querySelector('[data-nested-form-target="target"]')
    this.liveSecondarySpaceRows.forEach((row) => insertionPoint.before(row))
    this.liveSecondarySpaceRows = null
  }

  searchFromMap() {
    this.getCountryCodeFromCenter()
    super.searchFromMap()
  }

  async resultsTargetConnected() {
    this.scrollResultsToTop()
    this.trackVisibleVenues()
  }

  // InfoBox sizing for venue markers (overrides base hook).
  // Used for both individual marker clicks and clustered marker clicks.
  // Width 320px centered + 40px above the marker.
  infoBoxPixelOffset() {
    return new google.maps.Size(-320 / 2, -40)
  }

  // Tracking for clustered venue pins (overrides base hook)
  trackClusterMapPin(targetMarkerInfoWindow) {
    const { venueId, venueBoost } = targetMarkerInfoWindow.dataset
    if (this.trackedMapMarkerPinsList.indexOf(venueId) == -1) {
      analytics.track("Venue Search Map Pin", { venue_id: venueId, venue_boost: venueBoost })
      this.trackedMapMarkerPinsList.push(venueId)
    }
  }

  addMapListeners() {
    super.addMapListeners()

    google.maps.event.addListener(this.map, "idle", () => {
      if (this.mapMovedByUser && !this.boundsFitted) {
        this.resetFormElementAddress()
        this.searchFromMap()
        this.mapMovedByUser = false

        if (!this.hasTrackedMapMoved) {
          analytics.track("Venue Search Map Moved")
          this.hasTrackedMapMoved = true
        }
      } else {
        this.boundsFitted = false
      }
    })
  }

  // Add an infoWindow to a marker that displays a productInfoWindow
  // Close the infoWindow if another marker is clicked
  addInfoWindow(marker, productInfoWindow) {
    marker.addListener("click", () => {
      const { venueId, venueBoost } = productInfoWindow.dataset
      if (this.trackedMapMarkerPinsList.indexOf(venueId) == -1) {
        analytics.track("Venue Search Map Pin", { venue_id: venueId, venue_boost: venueBoost })
        this.trackedMapMarkerPinsList.push(productInfoWindow.dataset.venueId)
      }
    })

    marker.addListener("click", () => {
      // Close the infoWindow if the clicked marker is the same as the previous one
      if (this.infoBox?.position_ == marker.position) {
        this.infoBox?.close()
        this.infoBox = null
      } else {
        const infoWindowContent = productInfoWindow.children[0].cloneNode(true)

        infoWindowContent.addEventListener("click", (e) => {
          this.trackClickedVenue(e, { source: "map" })
        })

        // Close the previous infoWindow if it exists
        if (this.infoBox) this.infoBox?.close()

        // Reuse the infoWindow of the clicked marker if exists
        // Else create a new infoWindow
        if (marker.infoBox) {
          this.infoBox = marker.infoBox
          this.infoBox.open(this.map, this.marker)
        } else {
          this.infoBox = new InfoBox({
            alignBottom: true,
            pixelOffset: this.infoBoxPixelOffset(),
            infoBoxClearance: new google.maps.Size(10, 50),
            visible: true,
            closeBoxURL: "",
            zIndex: 10000,
          })
          this.infoBox.setContent(infoWindowContent)
          this.infoBox.setPosition(marker.position)
          this.infoBox.open(this.map, this.marker)
          marker.infoBox = this.infoBox
        }

        // Scroll the venue list to the corresponding venue
        // Highlight the venue in the list with a different color
        this.productListItemTargets.forEach((e) => e.children[0].classList.remove("pinned"))
        productInfoWindow.previousElementSibling.children[0].classList.add("pinned")
        productInfoWindow.previousElementSibling.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    })
  }

  // Tracking Methods
  trackVisibleVenues() {
    this.productListItemTargets.forEach((card) => {
      const { venueId, venueBoost, isSponsored, category, position, page } = card.children[0].dataset
      if (
        this.isScrolledIntoView &&
        this.isScrolledIntoView(card) &&
        this.trackedProductList.indexOf(venueId) == -1
      ) {
        const totalPosition = this.venuePosition(position, page)
        const properties = {
          venue_id: venueId,
          venue_boost: venueBoost,
          is_sponsored: isSponsored === "true",
          category,
          position: totalPosition,
          page,
          source: "listing",
        }
        analytics.track("Viewed Search Result", properties)
        this.trackedProductList.push(venueId)
      }
    })
  }

  trackClickedVenue(event, params = {}) {
    const isArrowClicked = event.target.closest(".swiper .control")
    const isButtonClicked = event.target.closest("button")

    if (isArrowClicked || isButtonClicked) {
      return
    }

    const element = event.currentTarget.querySelector(
      ".c--venue-list-item--v2, .c--venue--info-window"
    )
    const { venueId, position, page, venueBoost, isSponsored } = element.dataset
    const totalPosition = this.venuePosition(position, page)
    const properties = {
      venue_id: venueId,
      venue_boost: venueBoost,
      is_sponsored: isSponsored === "true",
      position: totalPosition,
      source: "list",
      ...params,
    }
    analytics.track("Search Result Clicked", properties)
  }

  venuePosition(position, page) {
    if (!position || !page) return
    return (page - 1) * VENUE_PER_PAGE + (Number(position) + 1)
  }

  secondarySpaceInputChanged(event) {
    const wrapper = event.target.closest('[class*="nested-form-wrapper"]')
    if (!wrapper) return

    this.pushFormParamsToUrl()
    this.submitFormFromModal()
  }

  secondarySpaceAdded() {
    this.pushFormParamsToUrl()
    this.submitFormFromModal()
  }

  removeSecondarySpace(event) {
    const wrapper = event.target.closest('[class*="nested-form-wrapper"]')
    if (!wrapper) return

    wrapper.remove()
    this.pushFormParamsToUrl()
    this.submitFormFromModal()
  }

  removeAllSecondarySpaces() {
    this.modalBodyTarget.querySelectorAll('[class*="nested-form-wrapper"]').forEach((wrapper) => wrapper.remove())
  }

  getCountryCodeFromCenter() {
    const geocoder = new google.maps.Geocoder();
    const center = this.map.getCenter();

    geocoder.geocode({ location: center }, (results, status) => {
      if (status === "OK") {
        if (results[0]) {
          const countryComponent = results[0].address_components.find(component =>
            component.types.includes("country")
          );

          if (countryComponent) {
            const countryCode = countryComponent.short_name;
            this.formTarget.elements.center_point_country_code.value = countryCode;

            const url = new URL(location);
            url.searchParams.set('center_point_country_code', countryCode);
            history.replaceState({}, '', url);
          }
        }
      }
    });
  }

  // Concierge venues are not as complete as other venues and therefore
  // do not appear if there are filters that are too specific.
  // As soon as a concierge filter is applied, we cancel all other filters.
  // We keep the hidden filters that correspond to the map boundaries.
  resetNonConciergeFilters() {
    Array.from(this.formTarget.elements).forEach((input) => {
      if (input.name.includes("concierge_type") || input.type === "hidden") return

      if (input.type === "checkbox" || input.type === "radio") {
        input.checked = false
      } else {
        input.value = ""
      }
    })
  }

}
